import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Select, { SelectOption } from '../primitives/Select/Select';
import UserMediaService from '../../../services/UserMediaService';
import './MediaSettings.css';
import Checkbox from '../primitives/Checkbox/Checkbox';
import Input from '../primitives/Input/Input';
import LocalMic from '../Mic/LocalMic';
import { MEDIA_SETTINGS_STORAGE_KEY } from '../../../utils/constants';
import { getStoredOrDefaultMediaSettings, getDefaultMediaSettings } from '../../../services/LocalStorage';

interface MediaSettingsProps {
  onShouldEnableMic?: (shouldEnable: boolean) => void;
  onShouldEnableCamera?: (shouldEnable: boolean) => void;
  onSelectedMicIdChange?: (selectedMicId: string) => void;
  onSelectedCameraIdChange?: (selectedMicId: string) => void;
  onUserNameChange?: (userName: string) => void;
}

const MediaSettings: React.FC<MediaSettingsProps> = ({
  onShouldEnableMic,
  onShouldEnableCamera,
  onSelectedMicIdChange,
  onSelectedCameraIdChange,
  onUserNameChange,
}) => {
  const [selectedMicId, setSelectedMicId] = useState<string | undefined>(getStoredOrDefaultMediaSettings().selectedMicId);
  const [selectedCameraId, setSelectedCameraId] = useState<string | undefined>(getStoredOrDefaultMediaSettings().selectedCameraId);
  const [audioDevices, setAudioDevices] = useState<SelectOption[]>([]);
  const [videoDevices, setVideoDevices] = useState<SelectOption[]>([]);
  const [micTrack, setMicTrack] = useState<MediaStreamTrack | undefined>();
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [userName, setUserName] = useState<string>(getStoredOrDefaultMediaSettings().userName);

  const [micPermissions, setMicPermissions] = useState<PermissionState | undefined>(undefined);
  const [cameraPermissions, setCameraPermissions] = useState<PermissionState | undefined>(undefined);

  const [isMicEnabled, setIsMicEnabled] = useState(getStoredOrDefaultMediaSettings().isMicEnabled);
  const [isCameraEnabled, setIsCameraEnabled] = useState(getStoredOrDefaultMediaSettings().isCameraEnabled);

  useEffect(() => {
    if (micPermissions === undefined && cameraPermissions === undefined) return;
    else if (micPermissions !== 'granted' || cameraPermissions !== 'granted') requestPermissions();
    else fetchInputDevices();
  }, [micPermissions, cameraPermissions]);

  const requestPermissions = useCallback(async () => {
    await UserMediaService.requestPermissions(micPermissions !== 'granted', cameraPermissions !== 'granted');
    checkForPermissions();
  }, [micPermissions, cameraPermissions]);

  const checkForPermissions = useCallback(async () => {
    const micPermissions = await UserMediaService.getMicPermissions();
    const cameraPermissions = await UserMediaService.getCameraPermissions();
    setMicPermissions(micPermissions);
    setCameraPermissions(cameraPermissions);
  }, []);

  const fetchInputDevices = useCallback(() => {
    UserMediaService.getInputDevices()
    .then(async (devices: { audioInputDevices: any; videoInputDevices: any; }) => {
      // console.log('got input devices from LMS in preview UE');
      const mics = devices.audioInputDevices;
      const cameras = devices.videoInputDevices;
      setAudioDevices(UserMediaService.getDeviceOptions(mics));
      setVideoDevices(UserMediaService.getDeviceOptions(cameras));
      // console.log(mics, cameras, micId, camId);
    })
    .catch((error: any) => console.error('Error setting input devices:', error));
  }, [isMicEnabled, isCameraEnabled, micPermissions, cameraPermissions]);

  useEffect(() => {
    checkForPermissions();

    return () => { }
  }, []);

  useEffect(() => {
    const settings = { isMicEnabled, isCameraEnabled, selectedMicId, selectedCameraId, userName };
    localStorage.setItem(MEDIA_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  }, [isMicEnabled, isCameraEnabled, selectedMicId, selectedCameraId, userName]);

  useEffect(() => {
    if (isMicEnabled && micPermissions === 'granted' && audioDevices.length > 0) getMicStream();
  }, [isMicEnabled, selectedMicId, micPermissions, audioDevices]);

  useEffect(() => {
    if (isCameraEnabled && cameraPermissions === 'granted' && videoDevices.length > 0) getCameraStream();
    else UserMediaService.stopVideoTrack();
  }, [isCameraEnabled, selectedCameraId, cameraPermissions, videoDevices]);

  const getMicStream = useCallback(async () => {
    try {
      const audioTrack = await UserMediaService.getUserMediaAudio();
      setMicTrack(audioTrack);
    } catch (e) {
      console.log('error getting mic stream', e);
    }
  }, []);

  const getCameraStream = useCallback(async () => {
    try {
      const videoStream = await UserMediaService.getUserMediaVideo();
      setCameraStream(new MediaStream([videoStream]));
    } catch (e) {
      console.log('error getting cam stream: ', selectedCameraId, e instanceof OverconstrainedError,  e);
      if (e instanceof OverconstrainedError) handleCameraChange('default');
    }
  }, [selectedCameraId]);

  const handleIsMicEnabled = useCallback((isChecked: boolean) => {
    setIsMicEnabled(isChecked);
    onShouldEnableMic && onShouldEnableMic(isChecked);
  }, [onShouldEnableMic]);

  const handleIsCameraEnabled = useCallback((isChecked: boolean) => {
    setIsCameraEnabled(isChecked);
    onShouldEnableCamera && onShouldEnableCamera(isChecked);
  }, [onShouldEnableCamera]);

  const updateUserName = useCallback((inputVal: string) => {
    setUserName(inputVal);
    onUserNameChange && onUserNameChange(inputVal);
  }, [onUserNameChange]);

  const handleMicChange = useCallback((value: string) => {
    if (value === '') return;
    UserMediaService.updateAudioInputDevice(value);
    setSelectedMicId(value);
    onSelectedMicIdChange && onSelectedMicIdChange(value);
  }, [onSelectedMicIdChange]);

  const handleCameraChange = useCallback((value: string) => {
    if (value === '') return;
    UserMediaService.updateVideoInputDevice(value);
    setSelectedCameraId(value);
    onSelectedCameraIdChange && onSelectedCameraIdChange(value);
  }, [isCameraEnabled, onSelectedCameraIdChange]);

  const cameraElement = useMemo(() => (
    <video
      ref={(video) => {
        if (video) {
          video.srcObject = cameraStream;
        }
      }}
      className='centered-video'
      autoPlay
      muted
    />
  ), [cameraStream]);

  const micElement = useMemo(() => (
    <div className='mic-wrapper'>
      <LocalMic audioTrack={micTrack} isEnabled={isMicEnabled} />
    </div>
  ), [micTrack, isMicEnabled]);

  const isMicEnabledElement = useMemo(() => (
    <Checkbox isChecked={isMicEnabled} onChange={(isChecked) => handleIsMicEnabled(isChecked)} label='Microphone' />
  ), [isMicEnabled, handleIsMicEnabled]);

  const isCameraEnabledElement = useMemo(() => (
    <Checkbox isChecked={isCameraEnabled} onChange={(isChecked) => handleIsCameraEnabled(isChecked)} label='Camera' />
  ), [isCameraEnabled, handleIsCameraEnabled]);

  const updateUserNameElement = useMemo(() => (
    <Input initialValue={userName} placeholder='Enter your name here' onChange={(updateVal) => updateUserName(updateVal)} />
  ), [userName, updateUserName]);

  const selectMicIdElement = useMemo(() => (
    <Select initialValue={selectedMicId} options={audioDevices} onChange={handleMicChange}/>
  ), [selectedMicId, audioDevices, handleMicChange]);

  const selectCameraIdElement = useMemo(() => (
    <Select initialValue={selectedCameraId} options={videoDevices} onChange={handleCameraChange} />
  ), [selectedCameraId, videoDevices, handleCameraChange]);

  return (
    <div className='root-media-settings'>
      <div className='container media-container'>
        <div className='camera-wrapper'>
          { cameraElement }
          { micElement }
        </div>
      </div>
      <div className='container'>
        { isMicEnabledElement }
        { isCameraEnabledElement }
      </div>
      <div className='container'>
        { updateUserNameElement }
      </div>
      <div className='container select-container'>
        { selectMicIdElement }
        { selectCameraIdElement }
      </div>
    </div>
  );
};

export default MediaSettings;
